import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  AddpCompanyDetails,
  AddpContactInfo,
  BillingAccountSummary,
  CrossConnect,
  PopSummary,
  ServiceDeliveryLocationDetails,
  ServiceItem
} from '../../../../services/service-delivery-locations.service';
import {
  AddpPricingParams,
  AddpPricingPayload,
  AddpPricingQuote,
  AddpPricingService
} from '../../../../services/addp-pricing.service';
import { ADDP_CURRENCIES } from '../../../../constants/addp.constants';
import { AuthenticationService } from '../../../../authenticationModule/services/authentication.service';

type PopLike = PopSummary | ServiceDeliveryLocationDetails['commercialPop'] | null;

type ConnectionPointPayload = {
  colocationSite: string | null;
  crossConnect: CrossConnect | null;
  encrypted: boolean | null;
};

@Component({
  selector: 'app-addp-cart-form',
  templateUrl: './addp-cart-form.component.html',
  styleUrls: ['./addp-cart-form.component.scss']
})
export class AddpCartFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() companyId!: number;
  @Input() pop: PopLike = null;
  @Input() xcoId!: string;
  @Input() serviceLabel!: string;
  @Input() selectedItems: ServiceItem[] = [];
  @Input() companyDetails: AddpCompanyDetails | null = null;
  @Input() loading = false;
  @Input() submitting = false;
  @Input() initialPayload: any | null = null;
  @Input() submitLabel = 'Confirm Extra XCO (ADDP) Request';

  @Output() cancel = new EventEmitter<void>();
  @Output() submitOrder = new EventEmitter<any>();

  form!: FormGroup;
  readonly currencyOptions: string[] = [...ADDP_CURRENCIES];
  private readonly defaultMckayEntityUuid = '3a6f7589-8973-3819-daac-512cfe725e16';
  private readonly continentEntityMap: Record<string, string> = {
    AM: '3a6f7589-8973-3819-daac-512cfe725e16',
    EU: 'ab167d5f-eb6b-69d1-37d2-51c413a78a90',
    AS: '44901b1f-06ff-05f1-2751-55fae82e338d'
  };

  pricingQuote: AddpPricingQuote | null = null;
  pricingLoading = false;
  pricingError: string | null = null;
  private currencySub: Subscription | null = null;
  private pricingSub: Subscription | null = null;
  private hasAppliedInitialPayload = false;
  private additionalBillingAccountOption: BillingAccountSummary | null = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly pricingService: AddpPricingService,
    private readonly authenticationService: AuthenticationService
  ) { }

  ngOnInit(): void {
    this.buildForm();
    if (this.companyDetails) {
      this.applyCompanyDetails(this.companyDetails);
    }
    this.prefillCurrency();
    this.setupCurrencyListener();
    this.applyInitialPayload();
    this.refreshPricing();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedItems']) {
      if (!changes['selectedItems'].firstChange) {
        this.prefillCurrency();
        this.prefillBillingAccountFromContractingAccount();
      }
      if (this.form) {
        this.refreshPricing();
      }
    }
    if (changes['companyDetails'] && this.companyDetails && this.form) {
      this.applyCompanyDetails(this.companyDetails);
      this.applyInitialPayload();
      this.refreshPricing();
    }
    if (changes['initialPayload'] && this.form) {
      this.additionalBillingAccountOption = null;
      this.hasAppliedInitialPayload = false;
      this.applyInitialPayload();
    }
    if (changes['pop']) {
      if (!changes['pop'].firstChange) {
        this.autoSelectMckayEntity();
      }
      if (this.form) {
        this.refreshPricing();
      }
    }
  }

  ngOnDestroy(): void {
    this.currencySub?.unsubscribe();
    this.pricingSub?.unsubscribe();
  }

  get hasDetails(): boolean {
    return !!this.companyDetails && !this.loading;
  }

  get companyName(): string {
    return this.companyDetails?.company.name || '';
  }

  get quoteName(): string {
    const popId = this.pop?.displayId || 'Unknown PoP';
    const today = new Date().toISOString().split('T')[0];
    return `${this.companyName} - ${this.serviceLabel} - ADDP at ${popId} [portal-${today}]`;
  }

  get colocationSite(): string {
    const persisted = this.payloadConnectionPoint?.colocationSite;
    if (persisted && persisted.trim().length > 0) {
      return persisted;
    }
    if (!this.pop) {
      return '';
    }
    const parts = [
      this.pop.displayId,
      this.pop.name,
      this.pop.city,
      this.pop.state,
      this.pop.country
    ].filter(Boolean);
    return parts.join(', ');
  }

  get technicalPop(): string {
    const persisted = this.payloadConnectionPoint?.crossConnect?.technicalPop?.technicalId;
    if (persisted) {
      return persisted;
    }
    const entry = this.selectedCrossConnect;
    return entry?.technicalPop?.technicalId || 'N/A';
  }

  get isSelfHosted(): string {
    const persisted = this.payloadConnectionPoint?.crossConnect?.selfHosted;
    if (typeof persisted === 'boolean') {
      return persisted ? 'Yes' : 'No';
    }
    const entry = this.selectedCrossConnect;
    if (!entry) {
      return 'No';
    }
    return entry?.selfHosted ? 'Yes' : 'No';
  }

  get thirdPartyHostAccount(): string {
    const persisted = this.payloadConnectionPoint?.crossConnect?.nameThirdPartyHost;
    if (persisted) {
      return persisted;
    }
    const entry = this.selectedCrossConnect;
    return entry?.nameThirdPartyHost || '';
  }

  get isEncrypted(): 'Y' | 'N' {
    const persisted = this.payloadConnectionPoint;
    if (persisted && typeof persisted.encrypted === 'boolean') {
      return persisted.encrypted ? 'Y' : 'N';
    }
    const item = this.referenceItem;
    return item?.isEncrypted ? 'Y' : 'N';
  }

  get msaName(): string {
    return this.referenceItem?.orderForm?.msaName || '';
  }

  get msaDisplayValue(): string {
    return this.msaName || '—';
  }

  get billingAccounts() {
    const accounts = this.companyDetails?.billingAccounts ?? [];
    if (this.additionalBillingAccountOption) {
      const exists = accounts.some(account =>
        account.uuid && this.additionalBillingAccountOption?.uuid && account.uuid === this.additionalBillingAccountOption.uuid
      );
      return exists ? accounts : [...accounts, this.additionalBillingAccountOption];
    }
    return accounts;
  }

  get formattedRecurringCost(): string {
    if (!this.pricingQuote) {
      return '';
    }
    return this.formatCurrency(this.pricingQuote.amount, this.pricingQuote.currency);
  }

  get isCompanySelectedMode(): boolean {
    if (!this.isMckayUser) {
      return false;
    }
    const targetCompanyId = this.companyDetails?.company?.id;
    const userCompanyId = this.authenticationService.getUserCurrent()?.company?.id;
    if (typeof targetCompanyId !== 'number') {
      return false;
    }
    if (typeof userCompanyId !== 'number') {
      return true;
    }
    return userCompanyId !== targetCompanyId;
  }

  get primaryContactHeading(): string {
    if (this.isCompanySelectedMode) {
      return 'Primary Contact (required)';
    }
    return 'Primary Contact (required - this person will be notified by email of this request and its updates)';
  }

  get companySelectedModeNotice(): string {
    return `You are currently requesting this ADDP on behalf of ${this.companyName}. The Primary Contact below will be notified by email of this request and its progress. Please make sure this information is correct and up to date.`;
  }

  onCancel(): void {
    this.cancel.emit();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue = this.form.value;
    const contactsGroup = formValue.contacts;

    const payload: any = {};
    if (this.shouldMarkAsAdminAddition()) {
      payload.addedByAdmin = true;
    }

    Object.assign(payload, {
      quantity: this.resolveQuantity(),
      company: this.companyDetails?.company,
      pop: this.pop,
      xco: this.xcoId,
      service: this.serviceLabel,
      currency: formValue.currency,
      items: this.selectedItems.map(item => ({
        id: item.id,
        popUuid: this.pop?.uuid || null,
        name: item.product?.name,
        carrier: item.product?.carrier
      })),
      orderForm: this.referenceItem?.orderForm ?? null,
      billingAccount: formValue.billingAccount,
      contacts: {
        primary: [contactsGroup.primary, contactsGroup.primary2],
        technical: [contactsGroup.technical, contactsGroup.technical2],
        networkNotification: [contactsGroup.networkNotification, contactsGroup.networkNotification2],
        billing: [contactsGroup.billing, contactsGroup.billing2],
        dataUsageReporting: [contactsGroup.dataUsageReporting, contactsGroup.dataUsageReporting2]
      },
      mckayEntity: formValue.mckayEntity,
      targetDate: formValue.targetDate,
      comments: formValue.comments,
      connectionPoint: this.buildConnectionPointPayload(),
      pricing: this.buildPricingPayload(formValue.currency)
    });

    this.submitOrder.emit(payload);
  }

  private buildForm(): void {
    this.form = this.fb.group({
      billingAccount: [null, Validators.required],
      mckayEntity: [''],
      currency: ['', Validators.required],
      contacts: this.fb.group({
        primary: this.buildContactGroup(true),
        primary2: this.buildContactGroup(),
        technical: this.buildContactGroup(),
        technical2: this.buildContactGroup(),
        networkNotification: this.buildContactGroup(),
        networkNotification2: this.buildContactGroup(),
        billing: this.buildContactGroup(),
        billing2: this.buildContactGroup(),
        dataUsageReporting: this.buildContactGroup(),
        dataUsageReporting2: this.buildContactGroup()
      }),
      targetDate: [''],
      comments: ['']
    });
  }

  private get isMckayUser(): boolean {
    return this.authenticationService.getUserCurrent()?.permissions.isMckayUser ?? false;
  }

  private buildContactGroup(emailRequired = false): FormGroup {
    const validators = emailRequired ? [Validators.required, Validators.email] : [Validators.email];
    return this.fb.group({
      name: ['', emailRequired ? [Validators.required] : []],
      email: ['', validators],
      phone: ['']
    });
  }

  private applyCompanyDetails(details: AddpCompanyDetails): void {
    if (details.billingAccounts.length === 1) {
      this.form.patchValue({ billingAccount: details.billingAccounts[0] });
    }
    this.prefillBillingAccountFromContractingAccount();
    this.prefillContacts(details.suggestedContacts);
    this.autoSelectMckayEntity();
  }

  private prefillContacts(suggested: AddpCompanyDetails['suggestedContacts']): void {
    this.patchContact('primary', suggested.primary || null);
    this.patchContact('technical', suggested.technical || null);
    this.patchContact('billing', suggested.billing || null);
    this.patchContact('dataUsageReporting', suggested.dataUsage || null);
    this.patchContact('networkNotification', suggested.notification || null);
  }

  private patchContact(key: string, info: AddpContactInfo | null): void {
    if (!info) {
      return;
    }
    const control = (this.form.get('contacts') as FormGroup).get(key);
    control?.patchValue({
      name: info.name,
      email: info.email,
      phone: info.phone || info.daytimePhone || info.mobilePhone || ''
    });
  }

  private prefillCurrency(): void {
    if (!this.form) {
      return;
    }
    const current = this.form.get('currency')?.value;
    if (current) {
      return;
    }
    const preferred = this.referenceItem?.orderForm?.currency ?? null;
    const fallback = this.fallbackCurrencyFromContinent(this.pop?.continent);
    const defaultCurrency = preferred || fallback || this.currencyOptions[0];
    this.form.patchValue({ currency: defaultCurrency });
  }

  private fallbackCurrencyFromContinent(continent?: string | null): string | null {
    if (!continent) {
      return null;
    }
    const normalized = continent.toUpperCase();
    const mapping: Record<string, string> = {
      AM: 'USD',
      EU: 'EUR',
      AS: 'JPY'
    };
    return mapping[normalized] ?? null;
  }

  private autoSelectMckayEntity(): void {
    if (!this.form || this.form.value.mckayEntity) {
      return;
    }
    if (!this.companyDetails?.mckayEntityCatalog?.length) {
      return;
    }
    const continent = this.pop?.continent ? this.pop.continent.toUpperCase() : '';
    const targetUuid = this.continentEntityMap[continent] || this.defaultMckayEntityUuid;
    const catalog = this.companyDetails.mckayEntityCatalog;
    const match = catalog.find(entry => entry.uuid === targetUuid) || catalog[0];
    if (match?.name) {
      this.form.patchValue({ mckayEntity: match.name });
    }
  }

  private prefillBillingAccountFromContractingAccount(): void {
    if (!this.form || this.form.value.billingAccount) {
      return;
    }
    const contractingAccountId = this.referenceItem?.orderForm?.contractingAccountId;
    if (!contractingAccountId || !this.billingAccounts.length) {
      return;
    }
    const match = this.billingAccounts.find(account => account.uuid === contractingAccountId);
    if (match) {
      this.form.patchValue({ billingAccount: match });
    }
  }

  /**
   * Business assumption: XCO items share the same underlying order form/MSA, so we reference the first item.
   */
  private get referenceItem(): ServiceItem | undefined {
    return this.selectedItems[0];
  }

  private get selectedCrossConnect(): CrossConnect | null {
    const item = this.referenceItem;
    if (!item?.crossConnects?.length) {
      return null;
    }
    return item.crossConnects.find(cc => cc.crossConnectId === this.xcoId) || null;
  }

  private setupCurrencyListener(): void {
    const control = this.form.get('currency');
    if (!control) {
      return;
    }
    this.currencySub = control.valueChanges.subscribe(() => {
      this.refreshPricing();
    });
  }

  private refreshPricing(): void {
    if (!this.form) {
      return;
    }
    if (!this.companyDetails?.company?.id || !this.pop?.id || !this.xcoId || !this.selectedItems.length) {
      this.pricingSub?.unsubscribe();
      this.pricingSub = null;
      this.pricingQuote = null;
      this.pricingLoading = false;
      this.pricingError = null;
      return;
    }
    const currency = this.form.get('currency')?.value;
    if (!currency) {
      this.pricingSub?.unsubscribe();
      this.pricingSub = null;
      this.pricingQuote = null;
      this.pricingLoading = false;
      this.pricingError = null;
      return;
    }
    const params: AddpPricingParams = {
      companyId: this.companyDetails.company.id,
      popId: this.pop.id,
      xcoId: this.xcoId,
      currency,
      serviceKey: this.referenceItem?.service ?? null,
      productIds: this.selectedItems
        .map(item => item.product?.id)
        .filter((id): id is number => typeof id === 'number')
    };
    this.pricingLoading = true;
    this.pricingError = null;
    this.pricingSub?.unsubscribe();
    this.pricingSub = this.pricingService.quoteAddp(params).subscribe({
      next: quote => {
        this.pricingQuote = quote;
        this.pricingLoading = false;
      },
      error: () => {
        this.pricingQuote = null;
        this.pricingLoading = false;
        this.pricingError = 'Unable to load pricing for this request.';
      }
    });
  }

  private applyInitialPayload(): void {
    if (!this.form || !this.initialPayload || this.hasAppliedInitialPayload) {
      return;
    }

    const payload = this.initialPayload;
    if (payload.currency) {
      this.form.patchValue({ currency: payload.currency });
    }
    if (payload.mckayEntity) {
      this.form.patchValue({ mckayEntity: payload.mckayEntity });
    }
    if (payload.billingAccount) {
      this.applyInitialBillingAccount(payload.billingAccount);
    }
    if (payload.contacts) {
      this.applyInitialContacts(payload.contacts);
    }
    if (payload.targetDate) {
      this.form.patchValue({ targetDate: payload.targetDate });
    }
    if (payload.comments !== undefined) {
      this.form.patchValue({ comments: payload.comments });
    }
    this.hasAppliedInitialPayload = true;
  }

  private applyInitialBillingAccount(initialAccount: BillingAccountSummary): void {
    const availableAccounts = this.companyDetails?.billingAccounts ?? [];
    const match = availableAccounts.find(account =>
      (initialAccount.uuid && account.uuid === initialAccount.uuid) ||
      (initialAccount.id && account.id === initialAccount.id)
    );
    if (match) {
      this.form.patchValue({ billingAccount: match });
      return;
    }
    this.additionalBillingAccountOption = initialAccount;
    this.form.patchValue({ billingAccount: initialAccount });
  }

  private applyInitialContacts(contacts: Record<string, any>): void {
    const definitions: Array<{ key: string; formGroups: [string, string] }> = [
      { key: 'primary', formGroups: ['primary', 'primary2'] },
      { key: 'technical', formGroups: ['technical', 'technical2'] },
      { key: 'networkNotification', formGroups: ['networkNotification', 'networkNotification2'] },
      { key: 'billing', formGroups: ['billing', 'billing2'] },
      { key: 'dataUsageReporting', formGroups: ['dataUsageReporting', 'dataUsageReporting2'] }
    ];
    definitions.forEach(def => {
      const values = Array.isArray(contacts[def.key]) ? contacts[def.key] : [];
      this.patchContactFromArray(def.formGroups[0], values[0]);
      this.patchContactFromArray(def.formGroups[1], values[1]);
    });
  }

  private patchContactFromArray(key: string, value: any): void {
    if (!value) {
      return;
    }
    const control = (this.form.get('contacts') as FormGroup).get(key);
    control?.patchValue({
      name: value.name || '',
      email: value.email || '',
      phone: value.phone || value.daytimePhone || value.mobilePhone || ''
    });
  }

  private formatCurrency(amount: number, currency: string): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(amount);
  }

  private buildPricingPayload(selectedCurrency: string): AddpPricingPayload {
    if (this.pricingQuote) {
      return {
        amount: this.pricingQuote.amount,
        currency: this.pricingQuote.currency,
        billingFrequency: this.pricingQuote.billingFrequency
      };
    }
    return {
      amount: null,
      currency: selectedCurrency,
      billingFrequency: 'monthly'
    };
  }

  private buildConnectionPointPayload(): ConnectionPointPayload {
    const crossConnect = this.selectedCrossConnect;
    return {
      colocationSite: this.colocationSite || null,
      crossConnect: crossConnect ? this.cloneCrossConnect(crossConnect) : null,
      encrypted: typeof this.referenceItem?.isEncrypted === 'boolean' ? Boolean(this.referenceItem?.isEncrypted) : null
    };
  }

  private get payloadConnectionPoint(): ConnectionPointPayload | null {
    if (this.initialPayload && this.initialPayload.connectionPoint) {
      return this.initialPayload.connectionPoint as ConnectionPointPayload;
    }
    return null;
  }

  private cloneCrossConnect(crossConnect: CrossConnect): CrossConnect {
    try {
      return JSON.parse(JSON.stringify(crossConnect));
    } catch (_error) {
      return crossConnect;
    }
  }

  private shouldMarkAsAdminAddition(): boolean {
    return this.isCompanySelectedMode;
  }

  private resolveQuantity(): number {
    const quantity = this.initialPayload?.quantity;
    if (!isNaN(Number(quantity))) {
      return Math.max(1, Number(quantity));
    }

    return 1;
  }

  isFieldInvalid(path: string): boolean {
    const control = this.form?.get(path);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  getFieldError(path: string): string | null {
    const control = this.form?.get(path);
    if (!control || !this.shouldShowValidation(control)) {
      return null;
    }
    if (control.hasError('required')) {
      if (path.endsWith('.name')) {
        return 'Name is required.';
      }
      if (path.endsWith('.email')) {
        return 'Email is required.';
      }
      return 'This field is required.';
    }
    if (control.hasError('email')) {
      return 'Please enter a valid email address.';
    }
    return null;
  }

  private shouldShowValidation(control: AbstractControl): boolean {
    return control.invalid && (control.dirty || control.touched);
  }
}
